import axios from 'axios';

/**
 * Dashboard Service
 * Aggregated data for the Home page. The backend should expose a single
 * endpoint that returns the full summary in one round-trip; see
 * `getHome` for the expected response shape.
 */
export const dashboardService = {
  /**
   * Get the aggregated Home dashboard summary.
   *
   * @param {Object} params
   * @param {number} [params.limit=5]      Max items returned per section
   * @param {string} [params.scope='me']   'me' returns the current user's
   *                                       tasks; 'organization' returns all.
   * @returns {Promise<{
   *   tasks: {
   *     items: Task[],            // Open tasks for the user, due_date asc, priority desc
   *     total_open: number,       // All open tasks for this user
   *     overdue_count: number     // Subset of total_open with due_date < today
   *   },
   *   submissions: {
   *     items: Submission[],      // Newest submissions, ungrouped
   *     total_new: number         // Count since last_seen_at OR last 24h fallback
   *   },
   *   proposals: {
   *     items: Proposal[],        // Open proposals (sent|viewed only), newest first
   *     total_open: number        // Count of all sent+viewed proposals in org
   *   },
   *   drafts: {
   *     items: Proposal[],        // Draft proposals (status='draft'), newest first
   *     total_open: number        // Count of all drafts in org
   *   },
   *   appointments: {
   *     items: Appointment[],     // Upcoming appointments, soonest first
   *     today_count: number,      // Today's appointments
   *     this_week_count: number   // Upcoming this calendar week (incl. today)
   *   }
   * }>}
   */
  async getHome(params = {}) {
    try {
      const response = await axios.get('/dashboard/home', {
        params: { limit: 5, scope: 'me', ...params },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching home dashboard:', error);
      throw error;
    }
  },
};

export default dashboardService;
